/** All user-facing strings for DV-2549 in one place. Vendor = registry displayName. */
export const integrationStatusCopy = {
  pill: { active: 'Active', disabled: 'Disabled', inactive: 'Inactive' },
  /** Vendor page header status line (icon + text). */
  header: { active: 'Integration is Active', disabled: 'Integration is Disabled', inactive: 'Integration is Inactive' },
  banner: {
    title: 'Integration Disabled',
    body: (vendor: string) =>
      `Syncing with ${vendor} is paused. Your credentials are saved, so you can turn it back on at any time.`,
    reenable: 'Re-enable Integration',
    reenabling: 'Reconnecting…',
    failedTitle: 'Re-enable failed',
  },
  dangerZone: {
    heading: 'Danger Zone',
    title: 'Disable Integration',
    body: (vendor: string) => `Turn off the two-way sync with ${vendor}.`,
    button: 'Disable Integration',
  },
  dialog: {
    title: (vendor: string) => `Disable ${vendor} Integration?`,
    bullets: (vendor: string) => [
      `Properties will no longer sync from ${vendor} into PlanOmatic.`,
      `Completed assets will no longer sync from PlanOmatic to ${vendor}.`,
    ],
    reassurance: (vendor: string) => `Your settings stay saved, so you can re-enable ${vendor} at any time.`,
    cancel: 'Cancel',
    confirm: 'Disable Integration',
    confirming: 'Disabling…',
  },
  toasts: {
    disabled: (vendor: string) => `${vendor} integration disabled`,
    reenabled: (vendor: string) => `${vendor} integration re-enabled`,
    changesSaved: 'Changes saved',
    saveFailed: "Couldn't save changes. Try again.",
  },
  saveLabels: {
    saveAndSync: 'Save & sync properties',
    saveChanges: 'Save changes',
    validating: 'Validating…',
  },
  properties: {
    syncPaused: 'Sync paused',
    refreshTooltip: (vendor: string) =>
      `Property sync is paused because the ${vendor} integration is disabled. Re-enable it from Integrations to refresh.`,
  },
  syncShare: {
    title: 'Integration Disabled',
    body: (vendor: string) =>
      `Syncing with ${vendor} is turned off, so these photos won't be delivered. Turn the integration back on from the Integrations page to resume syncing.`,
    /** DV-3345: for editors on the Global Orders photos page who cannot reach Integrations. */
    bodyAskAdmin: (vendor: string) =>
      `Syncing with ${vendor} is turned off, so these photos won't be delivered. Ask an Internal Admin to turn it back on to resume syncing.`,
    goToIntegrations: 'Go to Integrations',
  },
  syncBanner: {
    /** Right after THIS page's save started the sync. */
    running: 'Connected! Property sync is running. New properties appear as they arrive.',
    /** A sync started elsewhere (Properties header, landing hero, another tab). */
    runningPassive: 'Property sync is running. New properties appear as they arrive.',
    complete: 'Property sync complete.',
    viewProperties: 'View properties',
  },
  /** Read-only credential summary (spec §5.3). */
  credentials: {
    subdomain: (vendor: string) => `${vendor} subdomain`,
    apiKey: 'API Key',
    apiSecret: 'API Secret',
    connection: 'Connection',
    marketplace: (vendor: string) => `${vendor} marketplace`,
    /** Fallback when a stored record carries nothing renderable (review finding 5). */
    none: 'No credentials on file',
  },
  /** Edit-mode chrome (spec §5.2 / §5.4 / §5.5 / §5.6). */
  editMode: {
    edit: 'Edit Settings',
    cancel: 'Cancel',
    pasteKey: 'Paste new API Key',
    pasteSecret: 'Paste new API Secret',
    maskNote: 'Stored credentials stay hidden. Paste new values to replace them.',
    failedTitle: 'Connection failed',
    failedFirst: (vendor: string) =>
      `We couldn't connect to ${vendor} with these credentials. Check the details and try again. Nothing has been saved.`,
    failedUpdate: "These credentials didn't work, so your previous ones are still in place.",
  },
} as const;
